import React, { useLayoutEffect, useContext, useRef, useMemo, useEffect, useState } from 'react'
import { THREE } from 'expo-three'
import { useFrame } from '@react-three/fiber'
import Box2D from 'box2dweb'
import context from './context'

const b2FixtureDef = Box2D.Dynamics.b2FixtureDef;
const b2BodyDef = Box2D.Dynamics.b2BodyDef;
const b2Body = Box2D.Dynamics.b2Body;
const b2Vec2 = Box2D.Common.Math.b2Vec2;

const shapes = {
  circle: Box2D.Collision.Shapes.b2CircleShape,
  box: Box2D.Collision.Shapes.b2PolygonShape,
}

export function useApi () {
  const { world, refs, destroyQueue } = useContext(context)
  function impulse(uuid, state) {
    if (!refs.hasOwnProperty(uuid)) return
    const body = refs[uuid].body
    const { freeze } = body.GetUserData()
    if (!freeze) {
      body.SetUserData({ ...body.GetUserData(), impulse: state, rotate: !state })
    }
  }
  function rotate(uuid, state) {
    if (!refs.hasOwnProperty(uuid)) return
    const body = refs[uuid].body
    const { freeze } = body.GetUserData()
    if (!freeze) {
      body.SetUserData({ ...body.GetUserData(), rotate: state })
    }
  }
  function revertSpin(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const body = refs[uuid].body
    const userData = body.GetUserData()
    body.SetUserData({
      ...userData,
      spinFactor: userData.spinFactor * -1,
    })
  }
  function overchargePower(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    body.SetUserData({ ...body.GetUserData(), coil: true })
  }
  function dischargePower(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    body.SetUserData({ ...body.GetUserData(), coil: false })
  }
  function overchargeFreezer(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    body.SetUserData({ ...body.GetUserData(), freezer: true })
  }
  function dischargeFreeze(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    body.SetUserData({ ...body.GetUserData(), freezer: false })
  }
  function grow(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body, object} = refs[uuid]
    // const fixture = body.GetFixtureList()

    // grow Box2D radius
    body.GetFixtureList().m_shape.m_radius = body.GetFixtureList().m_shape.m_radius * 1.7
    body.SetUserData({ ...body.GetUserData(), hulk: 1 })
    // // grow ThreeJS scale
    // object.scale.set(1.5, 1.5, 1.5)
  }
  function freeze(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const body = refs[uuid].body
    body.SetUserData({ ...body.GetUserData(), rotate: false, freeze: true, impulse: false })
    object.userData.freeze = true
  }
  function unfreeze(uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body, object} = refs[uuid]
    body.SetUserData({ ...body.GetUserData(), rotate: true, freeze: false })
    object.userData.freeze = false
  }
  function hyperJump(uuid, { x = 0, y = 0 }) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    body.SetTransform({ position: {x, y}, GetAngle: () => body.GetAngle() })
  }
  function outOfThisWorld (uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {body} = refs[uuid]
    for (var f = body.GetFixtureList(); f; f = f.GetNext()) {
      // disable collision
      f.SetSensor(true)
    }
  }
  function destroy (uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    destroyQueue.push(refs[uuid].body)
  }
  function restart (uuid) {
    if (!refs.hasOwnProperty(uuid)) return
    const {object} = refs[uuid]
    const originPosition = object.userData.origin
    console.log(originPosition)
    const [x, y, z] = originPosition || [0,0,0];
    const origin = { x, y: z };
    const body = refs[uuid].body
    // remove forces
    body.SetLinearVelocity(new b2Vec2(0, 0))
    // restart angle and position
    body.SetTransform({ position: { ...origin }, GetAngle: () => body.GetAngle() })
    for (var f = body.GetFixtureList(); f; f = f.GetNext()) {
      // re-enable collision
      f.SetSensor(false)
    }
  }
  const createCharacter = (object, type, world) => {
    const fixtureDef = new b2FixtureDef()
    fixtureDef.shape = new shapes[type]()
    const bodyDef = new b2BodyDef();
    bodyDef.type = object.userData.static ? b2Body.b2_staticBody : b2Body.b2_dynamicBody;
    // fixtureDef.density = 1
    if (type === 'circle') {
      // if (!object.userData.radius) {
      //   object.children[1].geometry.computeBoundingBox()
      // }
      fixtureDef.shape.SetRadius(object.userData.radius || 0.5)
    }
    if (type === 'box') {
      fixtureDef.shape.SetAsBox(object.userData.hx || 0.5, object.userData.hy || 0.5)
    }

    if (!object.userData.static) {
      fixtureDef.density = object.userData.density || 1.0;
      fixtureDef.friction = object.userData.friction  || 1;
      fixtureDef.restitution = object.userData.restitution || 1;
      fixtureDef.isSensor = object.userData.pickable || false;

      bodyDef.linearDamping = 1
      bodyDef.bullet = true
    }
    

    bodyDef.position.Set(object.position.x, object.position.z)
    bodyDef.angle = object.userData.angle || 0

    const body = world.CreateBody(bodyDef)
    body.CreateFixture(fixtureDef);
    body.SetUserData({
      impulse: false,
      rotate: true,
      spinFactor: 1,
      ...object.userData,
      uuid: object.uuid,
    });
    // bodyDef.SetAngle(object.angle || 0)

    // refs[object.uuid] = {
    //   object,
    //   body,
    // }
    return body
  }

  function getPhysicsUserData (uuid) {
    if (!refs.hasOwnProperty(uuid)) return {}
    return refs[uuid].body.GetUserData()
  }
  return {
    impulse,
    rotate,
    freeze,
    unfreeze,
    destroy,
    restart,
    createCharacter,
    grow,
    revertSpin,
    overchargePower,
    dischargePower,
    overchargeFreezer,
    dischargeFreeze,
    hyperJump,
    outOfThisWorld,
    getPhysicsUserData,
  }
}

function useBody(
  type,
  config,
  fwdRef
) {
  const localRef = useRef(null)
  const ref = fwdRef ? fwdRef : localRef
  const api = useApi()
  const { world, refs } = useContext(context)
  // const [body, setBody] = useState()
  // const [object, setObject] = useState()
  // const [impulse, setImpulse] = useState(true)

  useEffect(() => {
    if (!ref.current || !world) {
      return
      // When the reference isn't used we create a stub
      // The body doesn't have a visual representation but can still be constrained
      // ref.current = new THREE.Object3D()
    }
    
    const object = ref.current
    if (!refs.hasOwnProperty(object.uuid)) {
      const body = api.createCharacter(object, type, world);
      refs[object.uuid] = { object, body }
    }
  }, [world])
  
  return [ref, api]
}

export function useCircle(config, fwdRef) {
  return useBody('circle', config, fwdRef)
}
export function useBox(config, fwdRef) {
  return useBody('box', config, fwdRef)
}